import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import changeColorClass from 'lumen/helpers/change-color-class';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import { formatVariance } from 'lumen/utils/market-snapshots/variance';

export interface MarketSnapshotsTableTdWithVarianceSignature {
  Args: {
    cellData?: {
      isValuePercent?: boolean;
      value: number | null;
      varianceType?: Variance;
      varianceValue?: number | null;
    };
  };
}

export default class MarketSnapshotsTableTdWithVariance extends Component<MarketSnapshotsTableTdWithVarianceSignature> {
  @service declare intl: Services['intl'];

  get formattedValue(): string {
    const value = this.args.cellData?.value;

    if (value === null || value === undefined) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(value, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: this.args.cellData?.isValuePercent ? 'percent' : 'decimal',
    });
  }

  get formattedVariance(): string {
    const varianceValue = this.args.cellData?.varianceValue;
    const varianceType = this.args.cellData?.varianceType ?? VARIANCES.NONE;
    const isPercentagePoint =
      varianceType === VARIANCES.ABSOLUTE && this.args.cellData?.isValuePercent;

    if (varianceValue === null || varianceValue === undefined) {
      if (varianceType === VARIANCES.PERCENTAGE) {
        return `${this.intl.t('general.missing')}%`;
      }

      if (isPercentagePoint) {
        return this.intl.t('general.percentage_point', {
          count: this.intl.t('general.missing'),
        });
      }

      return this.intl.t('general.missing');
    }

    return formatVariance(this.intl, varianceValue, {
      isValuePercent: Boolean(this.args.cellData?.isValuePercent),
      varianceType,
    });
  }

  get showVariance(): boolean {
    const varianceType = this.args.cellData?.varianceType ?? VARIANCES.NONE;

    return varianceType !== VARIANCES.NONE;
  }

  get varianceClass(): string {
    return changeColorClass(this.args.cellData?.varianceValue);
  }

  <template>
    <div class="flex flex-col tabular-nums {{if this.showVariance 'py-200'}}">
      <div class="truncate" data-test-id="value" title={{this.formattedValue}}>
        {{this.formattedValue}}
      </div>

      {{#if this.showVariance}}
        <div
          class="truncate {{this.varianceClass}}"
          data-test-id="variance"
          title={{this.formattedVariance}}
        >
          {{this.formattedVariance}}
        </div>
      {{/if}}
    </div>
  </template>
}
